// Why a care person opened a member they are not assigned to.
//
// A care person who is not on a member's care team may still have a legitimate
// reason to open their record. Recording that reason as free text makes the
// resulting audit unanswerable — "how often is clinical case review the
// reason?" becomes text mining rather than a GROUP BY — so the reason comes
// from this closed vocabulary instead.
//
// The codes are the ones Pelago 1 shipped, kept identical so audit history
// spanning the migration stays comparable rather than silently re-based on a
// new vocabulary. Order is the order both Pelago 1 sources use, and a surface
// rendering the options in array order inherits it.
//
// Codes only, deliberately. The human-readable labels are user-facing copy and
// belong in the localization catalog (ADR-017), keyed by these codes, added
// alongside the surface that renders them.

import { z } from 'zod';

export const ACCESS_JUSTIFICATION_REASON = [
  'scheduling',
  'care_team_assignment',
  'clinical_case_review',
  'clinical_documentation',
  'care_coordination',
  'eligibility_verification',
  'responding_to_member',
  'other',
] as const;

export type AccessJustificationReason = (typeof ACCESS_JUSTIFICATION_REASON)[number];

export const accessJustificationReasonSchema = z.enum(ACCESS_JUSTIFICATION_REASON);

/**
 * The one code whose accompanying behaviour differs: picking it requires the
 * caller to supply free text saying what the reason actually was. Exported so
 * the surfaces that special-case it branch on a name rather than on a bare
 * `'other'` literal — the rule travels with the vocabulary, and renaming the
 * code breaks the constant instead of silently orphaning three call sites.
 */
export const ACCESS_JUSTIFICATION_REASON_OTHER = 'other';

/**
 * The vocabulary for access justified INSIDE Galapagos: the Pelago 1 codes plus
 * the reasons only Galapagos workflows give. It is what `member_access_grants`
 * stores and what the grant write accepts.
 *
 * Kept apart from `ACCESS_JUSTIFICATION_REASON` rather than grown in place
 * because that list also feeds the Pelago 1 dialog, whose recorder writes
 * backend2's audit — and backend2 rejects any code it does not know. A
 * Galapagos-only reason added to the shared list would surface in that dialog
 * and fail every write that chose it.
 *
 * Derived by spreading, never restated, so the Pelago 1 codes stay a strict
 * prefix: a code added there reaches this list without a second edit, and the
 * two cannot disagree about the codes they share.
 */
export const P2_ACCESS_JUSTIFICATION_REASON = [
  ...ACCESS_JUSTIFICATION_REASON,
  'member_support_request',
  'escalation_handling',
] as const;

export type P2AccessJustificationReason = (typeof P2_ACCESS_JUSTIFICATION_REASON)[number];

export const p2AccessJustificationReasonSchema = z.enum(P2_ACCESS_JUSTIFICATION_REASON);
